import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Camera, ChevronLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ListingCard } from "@/components/ListingCard";
import { useCachedGet } from "@/lib/cache";
import { usePagedListings, LoadMoreSentinel } from "@/lib/pagedListings";
import { CATEGORIES, categoryName } from "@/lib/constants";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { ForYou } from "@/components/ForYou";
import { HomeRow } from "@/components/HomeRow";
import { useRecentlyViewed } from "@/lib/recentlyViewed";

const SKYLINE = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Home() {
  const { t, lang } = useLang();
  const ar = lang === "ar";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("categories");
  const [search, setSearch] = useState("");
  const category = searchParams.get("category") || "all";
  const subcategory = searchParams.get("subcategory") || "all";
  const params = {
    ...(category !== "all" ? { category } : {}),
    ...(subcategory !== "all" ? { subcategory } : {}),
  };
  const paged = usePagedListings(params);
  const { loading } = paged;
  const listings = paged.items;
  const offers = useCachedGet("/listings", { discounted: true }).data || [];
  const featured = useCachedGet("/listings", { featured: true, limit: 10 }).data?.items || [];
  const recent = useRecentlyViewed();

  const runSearch = (e) => { e.preventDefault(); navigate(search.trim() ? `/search?q=${encodeURIComponent(search.trim())}` : "/search"); };
  const nm = (item) => (lang === "en" ? item.name_en : item.name_ar);
  const filtered = category !== "all";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-8 w-full flex-1 pt-6 lg:pt-8">
        <h1 className={`font-headline ${ar ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"} font-extrabold text-[#1A5C3A] mb-4 lg:mb-5`} data-testid="home-greeting">{t("home_greeting")}</h1>

        {/* License banner — full-width image, text overlay only */}
        <div className="relative rounded-[26px] overflow-hidden h-40 lg:h-56 2xl:h-64 mb-4 lg:mb-5">
          <img src={SKYLINE} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 h-2/3" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)" }} />
          <div className="relative h-full flex flex-col justify-end p-5 lg:p-8 text-white" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.55)" }}>
            <h2 className={`font-headline ${ar ? "text-3xl" : "text-2xl"} font-extrabold mb-1`}>{t("markets_title")}</h2>
            <p className={`${ar ? "text-sm" : "text-xs"} text-white/90 leading-relaxed`}>{t("markets_sub")}</p>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={runSearch} className="bg-white rounded-full h-14 flex items-center gap-2 px-2 shadow-sm border border-[#EAE6DB] mb-6 lg:mb-8 lg:h-16 lg:px-3">
          <button type="button" onClick={() => navigate("/search")} className="w-10 h-10 rounded-full bg-[#EAF5F0] flex items-center justify-center text-[#1A5C3A] shrink-0" data-testid="home-camera-button">
            <Camera className="w-5 h-5" />
          </button>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search_product")} className={`flex-1 outline-none bg-transparent text-slate-800 ${ar ? "text-base" : "text-sm"}`} data-testid="hero-search-input" />
          <button type="submit" className={`h-10 px-5 rounded-full bg-[#1A5C3A] hover:bg-[#144a2e] transition-colors text-white ${ar ? "text-base" : "text-sm"} font-bold flex items-center gap-1.5 shrink-0`} data-testid="hero-search-button">
            <Search className="w-4 h-4" /> {t("search_btn")}
          </button>
        </form>

        {/* Categories / For you tabs */}
        <div className={`flex items-center justify-between mb-4 lg:mb-6 ${ar ? "flex-row-reverse" : ""}`} data-testid="home-tabs-row">
          <div className="flex gap-2 bg-white/70 rounded-full p-1" data-testid="home-tabs">
            {["categories", "foryou"].map((k) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`home-tab-${k}`}
                className={`px-4 ${ar ? "h-10 text-base" : "h-9 text-sm"} rounded-full font-bold transition-colors ${tab === k ? "bg-[#1A5C3A] text-white" : "text-slate-600 hover:text-[#1A5C3A]"}`}>
                {k === "categories" ? t("categories_title") : t("for_you")}
              </button>
            ))}
          </div>
          {tab === "categories" && (
            <button onClick={() => navigate("/categories")} className={`flex items-center gap-0.5 text-[#1A5C3A] ${ar ? "text-base" : "text-sm"} font-semibold`} data-testid="view-all-categories">
              {t("viewAll")} <ChevronLeft className="w-4 h-4" />
            </button>
          )}
        </div>
        {tab === "categories" ? (
          <div className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1 mb-8 lg:grid lg:grid-cols-5 lg:gap-6 lg:overflow-visible lg:mb-12" data-testid="home-categories-scroller">
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => navigate(`/category/${c.id}`)} data-testid={`home-category-${c.id}`} className="group flex flex-col items-center gap-2 lg:gap-3 shrink-0 basis-1/3 lg:basis-auto snap-start">
                <span className={`w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full overflow-hidden border-4 border-[#d4a843] lg:transition-transform lg:duration-300 lg:group-hover:-translate-y-1 ${category === c.id ? "ring-2 ring-[#1A5C3A] ring-offset-2 ring-offset-white" : ""}`}>
                  <img src={c.thumb} alt="" className="w-full h-full object-cover" />
                </span>
                <span className={`${ar ? "text-sm sm:text-base" : "text-[13px] sm:text-sm"} font-semibold text-center leading-tight text-[#1A5C3A] lg:text-base`}>{nm(c)}</span>
              </button>
            ))}
          </div>
        ) : (
          <ForYou />
        )}

        <HomeRow title={ar ? "شوهد مؤخراً" : "Recently viewed"} ads={recent.ads} ar={ar} testid="home-recent-section"
          action={<button onClick={recent.clear} className="text-sm font-bold text-slate-500 hover:text-red-600 transition-colors" data-testid="home-recent-clear">{ar ? "مسح" : "Clear"}</button>} />
        <HomeRow title={ar ? "إعلانات مميزة" : "Featured Ads"} ads={featured} ar={ar} testid="home-featured-section" />

        {/* Offers & Discounts */}
        <section className="mb-8 lg:mb-12" data-testid="home-offers-section">
          <div dir={ar ? "rtl" : "ltr"} className="flex items-center justify-between mb-4 lg:mb-5">
            <h3 className={`font-headline ${ar ? "text-xl text-right" : "text-lg text-left"} font-extrabold text-slate-900 lg:text-2xl`} data-testid="home-offers-title">{t("offers_title")}</h3>
          </div>
          {offers.length === 0 ? (
            <div className={`text-center py-6 text-slate-500 ${ar ? "text-base" : "text-sm"} bg-white/60 rounded-3xl`} data-testid="home-offers-empty">{t("offers_empty")}</div>
          ) : (
            <div className="flex gap-4 lg:gap-6 overflow-x-auto no-scrollbar pb-1" data-testid="home-offers-list">
              {offers.map((l) => <div key={l.id} className="w-44 sm:w-52 lg:w-[calc((100%-4.5rem)/4)] xl:w-[calc((100%-6rem)/5)] shrink-0"><ListingCard listing={l} big={ar} /></div>)}
            </div>
          )}
        </section>

        {/* Listings */}
        <div dir={ar ? "rtl" : "ltr"} className="flex items-center justify-between mb-4 lg:mb-5">
          <h3 className={`font-headline ${ar ? "text-xl text-right" : "text-lg text-left"} font-extrabold text-slate-900 lg:text-2xl`} data-testid="home-latest-title">{filtered ? categoryName(category, lang) : t("latestAds")}</h3>
          {filtered && (
            <button onClick={() => navigate("/")} className={`text-[#1A5C3A] ${ar ? "text-base" : "text-sm"} font-semibold`} data-testid="clear-filter">{t("all")}</button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6 lg:pb-12">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-3xl border border-[#EAE6DB] overflow-hidden animate-pulse"><div className="aspect-square bg-slate-200" /><div className="p-3.5 space-y-2"><div className="h-4 bg-slate-200 rounded" /><div className="h-4 w-1/2 bg-slate-200 rounded" /></div></div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="no-listings">{t("noListings")}</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6 lg:pb-12" data-testid="listings-grid">
            {listings.map((l) => <ListingCard key={l.id} listing={l} big={ar} />)}
          </div>
        )}
        <LoadMoreSentinel hasMore={paged.hasMore} busy={paged.busy} onMore={paged.loadMore} count={listings.length} />
      </main>

      <Footer />
    </div>
  );
}
